import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { TmsMastersDataService } from '../../../../../data-services/tms-masters-data.service';
import { UserSessionService } from '../../../../../ui-framework/global-services/user-session/user-session.service';
import { ConfigDataService } from '../../../../../data-services/config-data.service';
import { OrganizationUnitType } from '../../../../../data-services/api-models/shared-models';

@Component({
  selector: 'app-card-details',
  imports: [MatCardModule, MatButtonModule, CommonModule],
  templateUrl: './card-details.component.html',
  styleUrl: './card-details.component.scss',
  standalone: true,
  providers: [TmsMastersDataService, UserSessionService, ConfigDataService]
})
export class CardDetailsComponent implements OnInit {
  readonly totalShg = signal<number>(0);
  readonly totalSangha = signal<number>(0);
  readonly totalUpasangha = signal<number>(0);
  readonly totalCmtc = signal<number>(0);
  readonly officeName = signal<string>('Office');
  readonly userRole = signal<string>('User');
  readonly parentUnitName = signal<string>('');
  readonly managedUnitName = signal<string>('');
  readonly unitTypeName = signal<string>('');
  readonly isDistrictOrStateOffice = signal<boolean>(true);

  constructor(
    private readonly tmsMastersDataService: TmsMastersDataService,
    private readonly userSessionService: UserSessionService,
    private readonly configDataService: ConfigDataService
  ) {}

  ngOnInit(): void {
    this.loadOfficeName();
    this.loadUserRole();
    this.loadParentUnitName();
    this.loadManagedUnits();
    this.checkOfficeLevel();
    this.loadDashboardCounts();
  }

  private checkOfficeLevel(): void {
    const loginResponse = this.userSessionService.loginResponse;
    const offices = loginResponse?.userRights?.offices;

    let isStateOrDistrict = true;

    if (offices && offices.length > 0) {
      const officeObj = offices[0] as any;
      const unitType = officeObj.unitType;
      const desc = (officeObj.description || '').toLowerCase();

      if (
        unitType === OrganizationUnitType.State ||
        unitType === OrganizationUnitType.District ||
        unitType === OrganizationUnitType.HeadOffice ||
        unitType === OrganizationUnitType.DistrictOffice ||
        unitType === OrganizationUnitType.Organization ||
        unitType === OrganizationUnitType.DistrictCenter ||
        unitType === 5 || unitType === 10 || unitType === 200 || unitType === 300 || unitType === 600 || unitType === 700
      ) {
        isStateOrDistrict = true;
      } else if (
        unitType === OrganizationUnitType.Block ||
        unitType === OrganizationUnitType.BlockOffice ||
        unitType === OrganizationUnitType.Sangha ||
        unitType === OrganizationUnitType.SanghaOffice ||
        unitType === OrganizationUnitType.GramPanchayat ||
        unitType === 100 || unitType === 110 || unitType === 150 || unitType === 400 || unitType === 500
      ) {
        isStateOrDistrict = false;
      } else if (desc.includes('block') || desc.includes('sangha') || desc.includes('bmmu') || desc.includes('gp')) {
        isStateOrDistrict = false;
      } else if (desc.includes('state') || desc.includes('district') || desc.includes('smmu') || desc.includes('dmmu') || desc.includes('head')) {
        isStateOrDistrict = true;
      }
    }

    const typeName = (this.unitTypeName() || '').toLowerCase();
    if (typeName) {
      if (typeName.includes('block') || typeName.includes('sangha') || typeName.includes('bmmu') || typeName.includes('gp')) {
        isStateOrDistrict = false;
      } else if (typeName.includes('state') || typeName.includes('district') || typeName.includes('smmu') || typeName.includes('dmmu') || typeName.includes('head')) {
        isStateOrDistrict = true;
      }
    }

    this.isDistrictOrStateOffice.set(isStateOrDistrict);
  }

  private loadOfficeName(): void {
    // Get office name from logged-in user's baseLocation or first office in list
    const loginResponse = this.userSessionService.loginResponse;
    
    if (loginResponse?.userRights?.offices && loginResponse.userRights.offices.length > 0) {
      // Find the office matching the baseLocation ID
      if (loginResponse.userRights.baseLocation) {
        const baseOffice = loginResponse.userRights.offices.find(
          (office: any) => office.id === loginResponse.userRights!.baseLocation
        );
        if (baseOffice) {
          this.officeName.set(baseOffice.description || '');
          this.checkOfficeLevel();
          return;
        }
      }
      // If no matching base office, use the first office
      this.officeName.set(loginResponse.userRights.offices[0].description || '');
    } else {
      this.officeName.set('Office');
    }
    this.checkOfficeLevel();
  }

  private loadUserRole(): void {
    // Get user role from logged-in user
    const loginResponse = this.userSessionService.loginResponse;
    if (loginResponse?.userRights?.roles && loginResponse.userRights.roles.length > 0) {
      this.userRole.set(loginResponse.userRights.roles[0].description || '');
    } else {
      this.userRole.set('User');
    }
  }

  private loadParentUnitName(): void {
    // Get parent unit name for the office
    const loginResponse = this.userSessionService.loginResponse;
    const officeId = loginResponse?.userRights?.baseLocation;
    
    if (officeId) {
      this.configDataService.getOrganizationUnit(officeId).subscribe({
        next: (data) => {
          if (data?.parentUnitName) {
            this.parentUnitName.set(data.parentUnitName);
          } else {
            this.parentUnitName.set('');
          }
        },
        error: (error) => {
          console.error('Error loading parent unit name:', error);
          this.parentUnitName.set('');
        }
      });
    }
  }

  private loadManagedUnits(): void {
    // Get managed unit for the logged-in user's office
    const loginResponse = this.userSessionService.loginResponse;
    const officeId = loginResponse?.userRights?.baseLocation;
    
    if (officeId) {
      this.configDataService.getManagedUnits(officeId).subscribe({
        next: (data) => {
          if (data && data.length > 0) {
            // Extract the first (only) managed unit's description and type
            this.managedUnitName.set(data[0].description || '');
            this.unitTypeName.set(data[0].unitTypeName || '');
          } else {
            this.managedUnitName.set('');
            this.unitTypeName.set('');
          }
          this.checkOfficeLevel();
        },
        error: (error) => {
          console.error('Error loading managed unit:', error);
          this.managedUnitName.set('');
          this.unitTypeName.set('');
          this.checkOfficeLevel();
        }
      });
    }
  }

  private loadDashboardCounts(): void {
    // Load SHG count
    this.tmsMastersDataService.getShggroup().subscribe({
      next: (data) => {
        this.totalShg.set(data?.length || 0);
      },
      error: (error) => {
        console.error('Error loading SHG count:', error);
        this.totalShg.set(0);
      }
    });

    // Load Sangha count
    this.tmsMastersDataService.getSangha().subscribe({
      next: (data) => {
        this.totalSangha.set(data?.length || 0);
      },
      error: (error) => {
        console.error('Error loading Sangha count:', error);
        this.totalSangha.set(0);
      }
    });

    // Load Upasangha count
    this.tmsMastersDataService.getUpaSangha().subscribe({
      next: (data) => {
        this.totalUpasangha.set(data?.length || 0);
      },
      error: (error) => {
        console.error('Error loading Upasangha count:', error);
        this.totalUpasangha.set(0);
      }
    });

    // Load CMTC count
    this.tmsMastersDataService.getCmtc().subscribe({
      next: (data) => {
        this.totalCmtc.set(data?.length || 0);
      },
      error: (error) => {
        console.error('Error loading CMTC count:', error);
        this.totalCmtc.set(0);
      }
    });
  }
}
